import {
  advisorVersion,
  getAdvisorCoverage,
  parseAdvisorDraft as parseAdvisorHistoryDraft,
  type AdvisorCard,
  type AdvisorDraft,
} from 'shared/library/breed-advisor';

export const advisorHistoryLimit = 20;
const maxHistoryLength = 1_000_000;

type HistoryRecord = { id: string; createdAt: number; draft: AdvisorDraft };
export type AdvisorHistory = {
  version: 1;
  activeId: string;
  activeCreatedAt: number;
  entries: HistoryRecord[];
};
export type AdvisorHistoryEntry = {
  id: string;
  createdAt: number;
  updatedAt: number;
  answerCount: number;
  hasResults: boolean;
  topBreeds: AdvisorCard['breed'][];
};

function newId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

export function createAdvisorHistory(now = Date.now()): AdvisorHistory {
  return { version: 1, activeId: newId(), activeCreatedAt: now, entries: [] };
}

export { parseAdvisorHistoryDraft };

export function hasAdvisorHistoryContent(draft: AdvisorDraft) {
  return !!(
    draft.answers.length ||
    draft.favoriteInterest !== undefined ||
    draft.favoriteIds.length ||
    draft.freeText.trim() ||
    draft.rounds.length ||
    draft.sessionId
  );
}

function bounded(history: AdvisorHistory): AdvisorHistory {
  const entries = [...history.entries]
    .sort((a, b) => b.draft.updatedAt - a.draft.updatedAt)
    .slice(0, advisorHistoryLimit);
  while (entries.length && JSON.stringify({ ...history, entries }).length > maxHistoryLength)
    entries.pop();
  return { ...history, entries };
}

export function parseAdvisorHistory(
  stored: string | null,
  now = Date.now(),
): AdvisorHistory | null {
  if (!stored || stored.length > 20_000_000) return null;
  try {
    const value = JSON.parse(stored);
    if (
      value?.version !== advisorVersion ||
      typeof value.activeId !== 'string' ||
      !value.activeId.length ||
      value.activeId.length > 100 ||
      !Number.isFinite(value.activeCreatedAt) ||
      value.activeCreatedAt < 0 ||
      value.activeCreatedAt > now ||
      !Array.isArray(value.entries)
    )
      return null;
    const entries: HistoryRecord[] = [];
    for (const entry of value.entries.slice(0, 100)) {
      if (
        !entry ||
        typeof entry.id !== 'string' ||
        !entry.id.length ||
        entry.id.length > 100 ||
        entries.some((existing) => existing.id === entry.id) ||
        !Number.isFinite(entry.createdAt) ||
        entry.createdAt < 0 ||
        entry.createdAt > now
      )
        continue;
      const draft = parseAdvisorHistoryDraft(JSON.stringify(entry.draft), now);
      if (draft && hasAdvisorHistoryContent(draft))
        entries.push({ id: entry.id, createdAt: entry.createdAt, draft });
    }
    return bounded({
      version: 1,
      activeId: value.activeId,
      activeCreatedAt: value.activeCreatedAt,
      entries,
    });
  } catch {
    return null;
  }
}

export function archiveAdvisorHistory(
  history: AdvisorHistory,
  draft: AdvisorDraft,
): AdvisorHistory {
  if (!hasAdvisorHistoryContent(draft)) return history;
  return bounded({
    ...history,
    entries: [
      { id: history.activeId, createdAt: history.activeCreatedAt, draft },
      ...history.entries.filter((entry) => entry.id !== history.activeId),
    ],
  });
}

export function updateActiveAdvisorHistory(
  history: AdvisorHistory,
  draft: AdvisorDraft,
): AdvisorHistory {
  return history.entries.some((entry) => entry.id === history.activeId)
    ? archiveAdvisorHistory(history, draft)
    : history;
}

export function startAdvisorHistory(
  history: AdvisorHistory,
  draft: AdvisorDraft,
  now = Date.now(),
): AdvisorHistory {
  return { ...archiveAdvisorHistory(history, draft), activeId: newId(), activeCreatedAt: now };
}

export function openAdvisorHistory(history: AdvisorHistory, draft: AdvisorDraft, id: string) {
  const entry = history.entries.find((entry) => entry.id === id);
  if (!entry || id === history.activeId) return null;
  return {
    history: {
      ...archiveAdvisorHistory(history, draft),
      activeId: entry.id,
      activeCreatedAt: entry.createdAt,
    },
    draft: entry.draft,
  };
}

export function removeAdvisorHistory(history: AdvisorHistory, id: string): AdvisorHistory {
  return { ...history, entries: history.entries.filter((entry) => entry.id !== id) };
}

export function getAdvisorHistoryEntries(
  history: AdvisorHistory,
  draft: AdvisorDraft,
): AdvisorHistoryEntry[] {
  return updateActiveAdvisorHistory(history, draft).entries.map((entry) => {
    const round = [...entry.draft.rounds]
      .reverse()
      .find((round) => round.cards.length || round.favorites.length);
    const cards = round ? [...round.cards, ...round.favorites] : [];
    return {
      id: entry.id,
      createdAt: entry.createdAt,
      updatedAt: entry.draft.updatedAt,
      answerCount:
        getAdvisorCoverage(entry.draft.answers).answered +
        Number(entry.draft.favoriteInterest !== undefined) +
        Number(entry.draft.favoriteInterest === true && entry.draft.favoriteStage === 'done') +
        Number(entry.draft.rounds.length > 0),
      hasResults: !!cards.length,
      topBreeds: Array.from(
        new Map(cards.map((card) => [card.breed.id, card.breed])).values(),
      ).slice(0, 3),
    };
  });
}
